import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import StatusBar from './StatusBar';
import Currency from '../Currency';

import {
  APPLICATION_STATUSES,
  CASCO_REFERRAL_STATUSES,
  CASCO_STATUSES,
  REFERRAL_STATUSES,
} from '../../constants';
import { getClaimStatusTranslation } from '../../helpers/langHelpers';

import styles from './InvoiceDetails.module.scss';

const createRow = (label, value) => (
  <div className={styles.row}>
    <span className={styles.label}>
      {label}
      <span className={styles.colonSymbol} />
    </span>
    <span className={styles.value}>{value || '- - -'}</span>
  </div>
);

const resolveStatuses = ({ isCasco, type, examinationDate }) => {
  if (isCasco) {
    if (type === 'REPAIR') {
      return CASCO_REFERRAL_STATUSES;
    }
    return examinationDate
      ? CASCO_STATUSES
      : CASCO_STATUSES.filter((o) => o.text !== 'EXAMINATION');
  }

  return type === 'APPLICATION' ? APPLICATION_STATUSES : REFERRAL_STATUSES;
};

const InvoiceDetails = ({ data }) => {
  const invoice = data;
  const { t } = useTranslation();

  const statuses = resolveStatuses(invoice);

  const statusesWithDates = statuses
    .map((st) => ({
      text: st.text,
      date: invoice[st.datePropName],
    }))
    .reduceRight((acc, st, i, arr) => {
      // filter out status with empty date if the next status has a date
      if (!st.date && arr[i + 1] && arr[i + 1].date) {
        return acc;
      }
      return [st, ...acc];
    }, []);

  const {
    status,
    acceptDate,
    invoiceSettleDate,
    invoicePayDate,
    invoiceAmount,
    detailedStatus,
    invoiceName,
    serviceProvider,
    serviceName,
    invoiceCurrency,
    rejectedAmount,
    deductibleAmount,
    settledAmount,
    incompleteDocsAction,
    consultingTimeAction,
    isCasco,
    invoiceExpenseType,
    invoiceInputDate,
    declarationDate,
    assessmentDate,
    examinationDate,
    settlementDate,
    canceledDate,
    rejectedDate,
    riskLabel,
    coveragePercent,
    amountAfterCoverage,
    assessmentAmount,
  } = invoice;

  const createComment = (date, text) => (
    <div>
      <div className={styles.commentHeader}>
        <span>{t('claim.invoiceDetailLabels.comment')}</span>
        <span>{`(${date})`}</span>
      </div>
      <div>{text}</div>
    </div>
  );

  const infoColumnContent = () => {
    if (isCasco) {
      return (
        <>
          {createRow(t('claim.claimInfo.category'), t(`riskLabels.${riskLabel}`))}
          {/* show detailed status in all cases */}

          {createRow(
            t('claim.invoiceDetailLabels.status'),
            t(`claim.invoiceDetailedStatus.${invoice.detailedStatus}`),
          )}

          {status === 'REGISTRATION' && (
            <>{createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}</>
          )}

          {status === 'ACCEPTANCE' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.declarationDate'), declarationDate)}
            </>
          )}

          {status === 'ASSESSMENT' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.declarationDate'), declarationDate)}
              {createRow(t('claim.invoiceDetailLabels.assessmentDate'), assessmentDate)}
            </>
          )}

          {status === 'EXAMINATION' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.declarationDate'), declarationDate)}
              {createRow(t('claim.invoiceDetailLabels.assessmentDate'), assessmentDate)}
              {createRow(t('claim.invoiceDetailLabels.examinationDate'), examinationDate)}
            </>
          )}

          {status === 'SETTLEMENT' && detailedStatus === 'COMPENSATED' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.declarationDate'), declarationDate)}
              {createRow(t('claim.invoiceDetailLabels.assessmentDate'), assessmentDate)}
              {!!examinationDate
                && createRow(t('claim.invoiceDetailLabels.examinationDate'), examinationDate)}
              {createRow(t('claim.invoiceDetailLabels.settlementDate'), settlementDate)}
            </>
          )}

          {status === 'SETTLEMENT' && detailedStatus === 'CANCELED' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.canceledDate'), canceledDate)}
            </>
          )}

          {status === 'SETTLEMENT' && detailedStatus === 'REJECTED' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.rejectedDate'), rejectedDate)}
            </>
          )}

          {status === 'PAYMENT' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.declarationDate'), declarationDate)}
              {createRow(t('claim.invoiceDetailLabels.assessmentDate'), assessmentDate)}
              {!!examinationDate
                && createRow(t('claim.invoiceDetailLabels.examinationDate'), examinationDate)}
              {createRow(t('claim.invoiceDetailLabels.settlementDate'), settlementDate)}
              {createRow(t('claim.invoiceDetailLabels.invoicePayDate'), invoicePayDate)}
            </>
          )}

          {status === 'SENT_TO_REPAIR' && (
            <>
              {createRow(t('claim.invoiceDetailLabels.eventDate'), invoiceInputDate)}
              {createRow(t('claim.invoiceDetailLabels.declarationDate'), declarationDate)}
            </>
          )}
        </>
      );
    }

    return (
      <>
        {/* show detailed status in all cases */}
        {createRow(
          t('claim.invoiceDetailLabels.status'),
          t(`claim.invoiceDetailedStatus.${invoice.detailedStatus}`),
        )}

        {status === 'IN_REVIEW' && createRow(t('claim.invoiceDetailLabels.examDate'), acceptDate)}

        {(status === 'PROCESSED' || invoice.status === 'PAYMENT')
          && createRow(t('claim.invoiceDetailLabels.settleDate'), invoiceSettleDate)}

        {status === 'PAYMENT'
          && createRow(t('claim.invoiceDetailLabels.paymentDate'), invoicePayDate)}

        {status === 'REFERRED' && (
          <>
            {createRow(t('claim.invoiceDetailLabels.referralNumber'), invoiceName)}
            {createRow(t('claim.invoiceDetailLabels.givenDate'), acceptDate)}
            {createRow(t('claim.invoiceDetailLabels.serviceProvider'), serviceProvider)}
            {createRow(t('claim.invoiceDetailLabels.serviceName'), serviceName)}
          </>
        )}
      </>
    );
  };

  const paymentColumnContent = () => {
    if (isCasco) {
      return (
        <>
          {['ASSESSMENT', 'EXAMINATION', 'SETTLEMENT', 'PAYMENT', 'SENT_TO_REPAIR'].includes(
            status,
          ) && (
            <>
              {createRow(
                t('claim.invoiceDetailLabels.assessmentAmount'),
                <Currency value={assessmentAmount} currency={invoiceCurrency} withCurrencyLabel />,
              )}
              {coveragePercent < 100 && (
                <>
                  {createRow(
                    t('claim.invoiceDetailLabels.coveragePercent'),
                    <span>{`${coveragePercent} %`}</span>,
                  )}
                  {createRow(
                    t('claim.invoiceDetailLabels.amountAfterCoverage'),
                    <Currency
                      value={amountAfterCoverage}
                      currency={invoiceCurrency}
                      withCurrencyLabel
                    />,
                  )}
                </>
              )}
              {createRow(
                t('claim.invoiceDetailLabels.deductibleAmount'),
                <Currency
                  value={deductibleAmount}
                  currency={invoiceCurrency}
                  withCurrencyLabel
                  isNegativeValue
                />,
              )}
            </>
          )}
          {((detailedStatus === 'COMPENSATED' && invoiceExpenseType === '1')
            || detailedStatus === 'PAYMENT'
            || status === 'SENT_TO_REPAIR') && (
            <>
              {createRow(
                t('claim.invoiceDetailLabels.settledAmount'),
                <Currency value={settledAmount} currency={invoiceCurrency} withCurrencyLabel />,
                styles.finalValue,
              )}
            </>
          )}
        </>
      );
    }
    return (
      <>
        {status !== 'REFERRED'
          && createRow(
            t('claim.invoiceDetailLabels.invoiceAmount'),
            <Currency value={invoiceAmount} currency={invoiceCurrency} withCurrencyLabel />,
          )}

        {(status === 'PROCESSED' || status === 'PAYMENT') && (
          <>
            {(detailedStatus === 'DENIED' || detailedStatus === 'PARTIAL_COMPENSATION')
              && createRow(
                t('claim.invoiceDetailLabels.rejectedAmount'),
                <Currency
                  value={rejectedAmount}
                  currency={invoiceCurrency}
                  withCurrencyLabel
                  isNegativeValue
                />,
                styles.negativeValue,
              )}
            {createRow(
              t('claim.invoiceDetailLabels.deductibleAmount'),
              <Currency
                value={deductibleAmount}
                currency={invoiceCurrency}
                withCurrencyLabel
                isNegativeValue
              />,
              styles.negativeValue,
            )}
            {detailedStatus !== 'DENIED'
              && createRow(
                t('claim.invoiceDetailLabels.settledAmount'),
                <Currency value={settledAmount} currency={invoiceCurrency} withCurrencyLabel />,
                styles.finalValue,
              )}
          </>
        )}

        {status === 'REFERRED' && (
          <>
            {createRow(
              t('claim.invoiceDetailLabels.servicePrice'),
              <Currency value={invoiceAmount} currency={invoiceCurrency} withCurrencyLabel />,
            )}
            {createRow(
              t('claim.invoiceDetailLabels.deductibleAmount'),
              <Currency
                value={deductibleAmount}
                currency={invoiceCurrency}
                withCurrencyLabel
                isNegativeValue
              />,
              styles.negativeValue,
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className={styles.invoiceDetails}>
      <div className={styles.desktopContent}>
        <div className={styles.statusWrapper}>
          <StatusBar statuses={statusesWithDates} />
        </div>
      </div>

      <div className={styles.infoContainer}>
        <div className={styles.infoColumn}>{infoColumnContent()}</div>
        <div className={styles.paymentColumn}>{paymentColumnContent()}</div>
        <div className={styles.mobileContent}>
          <StatusBar statuses={statusesWithDates} />
        </div>
      </div>

      {!isCasco && (
        <div className={styles.commentContainer}>
          {status === 'RECEIVED'
            && invoice.incompleteDocsAction
            && createComment(incompleteDocsAction.actionDate, getClaimStatusTranslation(incompleteDocsAction, 'actionText'))}

          {status === 'REFERRED'
            && consultingTimeAction
            && createComment(
              consultingTimeAction.actionDate,
              getClaimStatusTranslation(consultingTimeAction, 'actionText'),
            )}
        </div>
      )}
    </div>
  );
};

InvoiceDetails.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    detailedStatus: PropTypes.string.isRequired,
    invoiceAmount: PropTypes.number,
    acceptDate: PropTypes.string,
    rejectedAmount: PropTypes.number,
    deductibleAmount: PropTypes.number,
    invoiceSettleDate: PropTypes.string,
    invoicePayDate: PropTypes.string,
    invoiceName: PropTypes.string,
    serviceName: PropTypes.string,
    serviceProvider: PropTypes.string,
    settledAmount: PropTypes.number,
    // TODO: change actionDate, actionText
    incompleteDocsAction: PropTypes.shape({}),
    consultingTimeAction: PropTypes.shape({}),
    isCasco: PropTypes.bool,
  }).isRequired,
};

export default InvoiceDetails;
